import React, { useCallback, useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Loader from "../Loader/Loader";
import swal from "sweetalert";
import { get_ApiManager } from "../ApiManager/ApiManager";
import {
  MY_WEBSITE_LEADS_COLUMNS,
  MY_WEBSITE_LEADS_COLUMNS_STORAGE_KEY,
  useLeadsColumnSettings,
} from "./websiteLeadsColumnSettings";
import {
  LeadsColumnSettingsModal,
  LeadsEditLeadModal,
} from "./websiteLeadsModals";
import {
  LeadsCountBadge,
  LeadsDateRangeFilters,
  LeadsFilterCard,
  LeadsFilterField,
  LeadsFilterInput,
  LEADS_FILTER_ICONS,
  LeadsGhostButton,
  LeadsMovedIcon,
  LeadsPageHeader,
  LeadsPageShell,
  LeadsPagination,
  LeadsPrimaryButton,
  LeadsRowIconButton,
  LeadsTableCard,
  LeadsTableToolbar,
  LeadsTableWrap,
  LeadsTruncatedText,
  LeadsFormTypeBadge,
  formatLeadDate,
  getLeadsTableColClass,
} from "./websiteLeadsShared";

function MyWebsiteLeads() {
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [items, setItems] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [order, setOrder] = useState("ASC");

  const {
    visibility,
    visibleColumns,
    hideableColumns,
    toggleColumn,
    resetToDefaults,
  } = useLeadsColumnSettings(
    MY_WEBSITE_LEADS_COLUMNS_STORAGE_KEY,
    MY_WEBSITE_LEADS_COLUMNS,
  );

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    contact: "",
    start_date: "",
    end_date: "",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });

  useEffect(() => {
    const storedUserId = JSON.parse(localStorage.getItem("user_id"));
    if (storedUserId) {
      setUserId(storedUserId);
    }
  }, []);

  const fetchMyLeads = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        perPage: pagination.perPage,
        page: pagination.currentPage,
        ...filters,
      };
      const resp = await apiUrl.get(`getWebsiteLeadsByUser/${userId}`, {
        params,
      });
      if (resp?.data?.data) {
        const { items: dataItems, meta: dataMeta } = resp.data.data;
        setItems(dataItems);
        setPagination((prev) => ({
          ...prev,
          total: dataMeta.total,
          lastPage: dataMeta.last_page,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [userId, filters, pagination.currentPage, pagination.perPage]);

  useEffect(() => {
    if (userId && pagination.currentPage > 0) {
      fetchMyLeads();
    }
  }, [userId, pagination.currentPage, pagination.perPage, fetchMyLeads]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = async () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        perPage: pagination.perPage,
        page: 1,
        ...filters,
      };
      const resp = await apiUrl.get(`getWebsiteLeadsByUser/${userId}`, {
        params,
      });
      if (resp?.data?.data) {
        const { items: dataItems, meta: dataMeta } = resp.data.data;
        setItems(dataItems);
        setPagination((prev) => ({
          ...prev,
          total: dataMeta.total,
          lastPage: dataMeta.last_page,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = async () => {
    setFilters({
      name: "",
      email: "",
      contact: "",
      start_date: "",
      end_date: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        perPage: pagination.perPage,
        page: 1,
        name: "",
        email: "",
        contact: "",
        start_date: "",
        end_date: "",
      };
      const resp = await apiUrl.get(`getWebsiteLeadsByUser/${userId}`, {
        params,
      });
      if (resp?.data?.data) {
        const { items: dataItems, meta: dataMeta } = resp.data.data;
        setItems(dataItems);
        setPagination((prev) => ({
          ...prev,
          total: dataMeta.total,
          lastPage: dataMeta.last_page,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (selectedItem) => {
    setEditLead(null);
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  const handlePerPageChange = (e) => {
    setEditLead(null);
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value, 10),
      currentPage: 1,
    }));
  };

  const sorting = (col) => {
    if (!col) return;
    const sorted = [...items].sort((a, b) => {
      const valA = a[col]?.toString().toLowerCase() || "";
      const valB = b[col]?.toString().toLowerCase() || "";
      if (order === "ASC") {
        return valA > valB ? 1 : -1;
      }
      return valA < valB ? 1 : -1;
    });
    setItems(sorted);
    setOrder(order === "ASC" ? "DESC" : "ASC");
    setSortColumn(col);
  };

  const openEditModal = (item) => {
    if (item.moved === 1) return;
    setEditLead(item);
  };

  const closeEditModal = () => {
    if (savingEdit) return;
    setEditLead(null);
  };

  const saveLead = async (id, draft) => {
    setSavingEdit(true);
    try {
      const response = await apiUrl.patch(`updateWebsiteLead/${id}`, draft);
      if (response?.data?.status) {
        setItems((prev) =>
          prev.map((item) => (item.id === id ? { ...item, ...draft } : item)),
        );
        setEditLead(null);
      } else {
        swal("Error", response?.data?.message || "Failed to save lead.", "error");
      }
    } catch (error) {
      swal(
        "Error",
        error?.response?.data?.message || "Failed to save lead.",
        "error",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const moveWebsiteLeads = async (id) => {
    const confirm = await swal({
      title: "Move to Client?",
      text: "This lead will be converted to a client.",
      icon: "warning",
      buttons: true,
    });
    if (!confirm) return;

    get_ApiManager(
      id,
      "moveWebsiteLeads",
      () => {
        swal("Success", "Lead moved to clients successfully.", "success");
        setEditLead(null);
        fetchMyLeads();
      },
      () => {
        swal("Error", "Failed to move lead to client.", "error");
      },
    );
  };

  const renderCell = (column, item) => {
    switch (column.key) {
      case "id":
        return item.id;
      case "name":
        return (
          <span className="fw-semibold text-dark" title={item.name}>
            {item.name}
          </span>
        );
      case "email":
        return item.email;
      case "phone":
        return item.phone;
      case "country":
        return item.country;
      case "destination":
        return item.destination;
      case "inquiry":
        return <LeadsTruncatedText text={item.inquiry} />;
      case "type":
        return <LeadsFormTypeBadge type={item.type} />;
      case "notes":
        return <LeadsTruncatedText text={item.notes} empty="—" />;
      case "created_at":
        return formatLeadDate(item.created_at);
      case "assigned_at":
        return formatLeadDate(item.assigned_at);
      case "actions":
        return (
          <div className="d-flex align-items-center gap-1">
            {item.moved === 1 ? (
              <LeadsMovedIcon />
            ) : (
              <>
                <LeadsRowIconButton
                  icon={FaIcons.FaEdit}
                  onClick={() => openEditModal(item)}
                  title="Edit lead"
                  variant="secondary"
                />
                <LeadsRowIconButton
                  icon={FaIcons.FaUserPlus}
                  onClick={() => moveWebsiteLeads(item.id)}
                  title="Move to client"
                  variant="primary"
                />
              </>
            )}
          </div>
        );
      default:
        return item[column.key] ?? "—";
    }
  };

  return (
    <LeadsPageShell>
      <LeadsPageHeader
        icon={FaIcons.FaUserFriends}
        title="My Website Leads"
        subtitle="View and manage leads assigned to you"
      />

      <LeadsFilterCard
        columns={3}
        headerRight={
          <LeadsCountBadge count={pagination.total} label="assigned" />
        }
        actions={
          <>
            <LeadsGhostButton onClick={clearAllFilters}>Clear</LeadsGhostButton>
            <LeadsPrimaryButton onClick={handleSearch}>
              <FaIcons.FaSearch size={11} /> Search
            </LeadsPrimaryButton>
          </>
        }
      >
        <LeadsFilterField label="Name" id="my-name">
          <LeadsFilterInput
            id="my-name"
            icon={LEADS_FILTER_ICONS.name}
            placeholder="Search name"
            value={filters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
          />
        </LeadsFilterField>
        <LeadsFilterField label="Email" id="my-email">
          <LeadsFilterInput
            id="my-email"
            type="email"
            icon={LEADS_FILTER_ICONS.email}
            placeholder="Search email"
            value={filters.email}
            onChange={(e) => handleFilterChange("email", e.target.value)}
          />
        </LeadsFilterField>
        <LeadsFilterField label="Contact" id="my-contact">
          <LeadsFilterInput
            id="my-contact"
            icon={LEADS_FILTER_ICONS.phone}
            placeholder="Phone number"
            value={filters.contact}
            onChange={(e) => handleFilterChange("contact", e.target.value)}
          />
        </LeadsFilterField>
        <LeadsDateRangeFilters
          startId="my-start-date"
          endId="my-end-date"
          startValue={filters.start_date}
          endValue={filters.end_date}
          onChange={handleFilterChange}
        />
      </LeadsFilterCard>

      <LeadsTableCard
        loading={loading}
        loader={<Loader />}
        toolbar={
          <LeadsTableToolbar
            title="Assigned Leads"
            onSettingsClick={() => setShowColumnSettings(true)}
          />
        }
      >
        <LeadsTableWrap>
          <table className="website-leads-table">
            <thead>
              <tr>
                {visibleColumns.map((header) => (
                  <th
                    key={header.key}
                    className={getLeadsTableColClass(header.key)}
                    onClick={() => sorting(header.dataKey)}
                    style={header.dataKey ? { cursor: "pointer" } : undefined}
                  >
                    {header.title}
                    {sortColumn === header.dataKey && (
                      <span>{order === "ASC" ? " ▲" : " ▼"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item) => (
                  <tr key={item.id}>
                    {visibleColumns.map((column) => (
                      <td
                        key={`${item.id}-${column.key}`}
                        className={getLeadsTableColClass(column.key)}
                        title={
                          ["email", "phone", "country", "destination", "name"].includes(
                            column.key,
                          )
                            ? item[column.key] || undefined
                            : undefined
                        }
                      >
                        {renderCell(column, item)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="text-center text-muted"
                    style={{ padding: "2rem 0.5rem", fontSize: 11 }}
                  >
                    No assigned leads found. Try adjusting your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </LeadsTableWrap>
      </LeadsTableCard>

      <LeadsPagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
      />

      <LeadsColumnSettingsModal
        show={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={hideableColumns}
        visibility={visibility}
        onToggle={toggleColumn}
        onReset={resetToDefaults}
      />

      <LeadsEditLeadModal
        show={Boolean(editLead)}
        lead={editLead}
        saving={savingEdit}
        onClose={closeEditModal}
        onSave={saveLead}
      />
    </LeadsPageShell>
  );
}

export default MyWebsiteLeads;
